import { useEffect, useState } from 'react';
import { Link, createFileRoute } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import {
  AlertCircle,
  AlertTriangle,
  ArrowLeft,
  ExternalLink,
  Lock,
  RefreshCw,
  Save,
} from 'lucide-react';
import type { PageLayout } from '@news-cms/shared';
import { EMPTY_PAGE_LAYOUT } from '@news-cms/shared';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button, buttonVariants } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { PageLayoutEditorV3 } from '@/components/page-layout-v3/page-layout-editor-v3';
import { collectSlotsClient } from '@/lib/page-layout-walk';
import { SuperAdminFallback } from '@/components/auth/super-admin-fallback';
import { useSession } from '@/lib/auth';
import { hasPermission } from '@/lib/permissions';
import type { OrphanedOverride } from '@/lib/pages';
import { pageDetailQueryOptions, pageErrorMessage, useUpdatePage } from '@/lib/pages';

export const Route = createFileRoute('/_authenticated/pages/$id/edit')({
  component: EditPagePage,
});

function EditPagePage() {
  const { id } = Route.useParams();
  const { user } = useSession();
  const canManage = hasPermission(user, 'settings.update');
  const detailQuery = useQuery(pageDetailQueryOptions(id));
  const update = useUpdatePage(id);

  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [layout, setLayout] = useState<PageLayout>(EMPTY_PAGE_LAYOUT);
  const [orphanedAfterSave, setOrphanedAfterSave] = useState<OrphanedOverride[]>([]);

  // Sync draft from server.
  useEffect(() => {
    if (!detailQuery.data) return;
    setName(detailQuery.data.name);
    setDescription(detailQuery.data.description ?? '');
    setLayout(detailQuery.data.layout);
  }, [detailQuery.data]);

  if (!user || user.role.slug !== 'super_admin') return <SuperAdminFallback />;
  if (!canManage) {
    return (
      <div className="p-8 text-center text-sm text-slate-500">
        ไม่มีสิทธิ์เข้าถึง — ต้องมี settings.update
      </div>
    );
  }

  if (detailQuery.isLoading || !detailQuery.data) {
    return <div className="p-8 text-center text-sm text-slate-400">กำลังโหลด page…</div>;
  }

  const page = detailQuery.data;
  const isDirty =
    name.trim() !== page.name ||
    description.trim() !== (page.description ?? '') ||
    JSON.stringify(layout) !== JSON.stringify(page.layout);

  const handleSave = () => {
    // Drop invalid slots (missing group) on the client so the backend's
    // VALIDATION_ERROR message doesn't surprise admin.
    const invalid = collectSlotsClient(layout).filter((s) => !s.groupSlug);
    if (invalid.length > 0) {
      window.alert(`มี ${invalid.length} slot ที่ยังไม่ได้เลือก group — เลือกก่อนบันทึก`);
      return;
    }
    update.mutate(
      {
        name: name.trim(),
        description: description.trim() || null,
        layout,
      },
      {
        onSuccess: (result) => setOrphanedAfterSave(result.orphanedOverrides),
      },
    );
  };

  const handleReset = () => {
    if (isDirty && !window.confirm('ยกเลิกการแก้ไขที่ยังไม่ได้บันทึก?')) return;
    setName(page.name);
    setDescription(page.description ?? '');
    setLayout(page.layout);
  };

  return (
    <div className="p-4 sm:p-8">
      <div className="mx-auto max-w-5xl space-y-6">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <div className="flex items-center gap-2">
              <Link to="/pages" className={buttonVariants({ variant: 'ghost', size: 'sm' })}>
                <ArrowLeft className="mr-1 h-4 w-4" /> Pages
              </Link>
              <h1 className="text-2xl font-bold tracking-tight text-slate-900 dark:text-slate-50">
                {page.name}
              </h1>
              {page.isSystem && (
                <span className="inline-flex items-center gap-1 rounded-full bg-slate-200 px-2 py-0.5 text-[10px] text-slate-600 dark:bg-slate-700 dark:text-slate-300">
                  <Lock className="h-2.5 w-2.5" /> system
                </span>
              )}
            </div>
            <p className="mt-1 text-xs text-slate-500">
              public URL: <code>/api/public/pages/{page.slug}</code>
              <a
                href={`/api/public/pages/${page.slug}`}
                target="_blank"
                rel="noopener noreferrer"
                className="ml-1 inline-flex items-center text-slate-400 hover:text-slate-600"
              >
                <ExternalLink className="h-3 w-3" />
              </a>
            </p>
          </div>
          {/* Fixed to viewport so the operator can scroll the (tall)
              layout builder without losing easy access to Save/Cancel.
              `sticky` doesn't work here because the parent flex header
              is short — once scrolled past, sticky leaves its scope.
              z-40 stays above the slot/carousel highlights (z-30) but
              below modal overlays. Right-6 clears the page padding;
              top-4 keeps it under any global app header. */}
          <div className="fixed right-6 top-16 z-40 flex items-center gap-2 rounded-lg border border-slate-200 bg-white/90 px-2 py-1.5 shadow-md backdrop-blur-sm dark:border-slate-700 dark:bg-slate-900/85">
            <Button
              variant="outline"
              size="sm"
              onClick={handleReset}
              disabled={!isDirty || update.isPending}
            >
              <RefreshCw className="mr-1.5 h-3.5 w-3.5" /> ยกเลิก
            </Button>
            <Button onClick={handleSave} disabled={!isDirty || update.isPending}>
              {update.isPending ? (
                <>
                  <RefreshCw className="mr-1.5 h-3.5 w-3.5 animate-spin" /> กำลังบันทึก
                </>
              ) : (
                <>
                  <Save className="mr-1.5 h-3.5 w-3.5" /> บันทึก
                  {isDirty && <span className="ml-1 text-[10px]">●</span>}
                </>
              )}
            </Button>
          </div>
        </div>

        {update.isError && (
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>{pageErrorMessage(update.error)}</AlertDescription>
          </Alert>
        )}

        {orphanedAfterSave.length > 0 && (
          <div className="rounded-md border border-amber-300 bg-amber-50 p-3 text-xs text-amber-800 dark:border-amber-900/60 dark:bg-amber-950/30 dark:text-amber-300">
            <div className="flex items-start gap-2">
              <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
              <div className="space-y-1">
                <div className="font-semibold">
                  มี {orphanedAfterSave.length} PR (ข่าวประชาสัมพันธ์) ที่ slot หาย — layout
                  เพิ่งบันทึกไม่มี slot id ที่ PR ชี้ถึง
                </div>
                <div className="text-amber-700 dark:text-amber-400">
                  Slot ids:{' '}
                  {orphanedAfterSave.map((o, i) => (
                    <span key={o.id}>
                      <code className="rounded bg-amber-100 px-1 text-[10px] dark:bg-amber-900/40">
                        {o.slotId}
                      </code>
                      {i < orphanedAfterSave.length - 1 ? ', ' : ''}
                    </span>
                  ))}
                </div>
                <div>
                  <Link
                    to="/promos"
                    className="text-amber-900 underline hover:text-amber-700 dark:text-amber-200 dark:hover:text-amber-100"
                  >
                    ไปจัดการที่หน้า /promos →
                  </Link>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Meta section */}
        <section className="rounded-lg border border-slate-200 bg-white p-5 dark:border-slate-800 dark:bg-slate-900">
          <h2 className="mb-4 text-sm font-semibold text-slate-700 dark:text-slate-200">
            ข้อมูล Page
          </h2>
          <div className="grid gap-3 sm:grid-cols-2">
            <div className="space-y-1.5">
              <Label htmlFor="meta-name">ชื่อ</Label>
              <Input
                id="meta-name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                maxLength={120}
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="meta-slug">Slug</Label>
              <Input
                id="meta-slug"
                value={page.slug}
                readOnly
                title={page.isSystem ? 'system page slug ห้ามแก้' : 'แก้ slug ผ่าน API'}
                className="bg-slate-50 dark:bg-slate-800"
              />
            </div>
            <div className="space-y-1.5 sm:col-span-2">
              <Label htmlFor="meta-desc">คำอธิบาย</Label>
              <Input
                id="meta-desc"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="หมายเหตุสำหรับทีม admin (ไม่ขึ้น public)"
                maxLength={500}
              />
            </div>
          </div>
        </section>

        {/* Layout section */}
        <section>
          <h2 className="mb-3 text-sm font-semibold text-slate-700 dark:text-slate-200">
            Layout — Visual Builder (Container / Row / Column / Slot)
          </h2>
          <PageLayoutEditorV3 draft={layout} onChange={setLayout} />
        </section>
      </div>
    </div>
  );
}
